import useModalState from "@logics/hooks/useModalState";
import { ToastError, ToastSuccess } from "@logics/utils/toast";
import { ERROR_MESSAGE } from "@domains/common/constants/toastMessages";
import ConfirmModal from "@domains/common/views/modal/ConfirmModal/ConfirmModal";

import { COMMENT_MESSAGE } from "../constants/toastMessages";
import {
  useDeleteReplyMutation,
  useUpdateReplyMutation,
} from "../network/projectMutations";
import CommentForm from "../views/components/CommentForm/CommentForm";
import CommentMenuBottomSheet from "../views/components/CommentMenuBottomSheet/CommentMenuBottomSheet";
import CommentOtherMenuBottomSheet from "../views/components/CommentOtherMenuBottomSheet/CommentOtherMenuBottomSheet";

interface UseReplyMenuProps {
  isMySelf: boolean;
  projectId?: string;
  commentId?: number;
  commenterName?: string;
  replyId?: number;
  payload?: string;
}

/**
 * replyCard에 사용되는 햄버거 메뉴의 항목에 해당하는 기능들이 있는 Hook
 */
export const useReplyMenu = ({
  isMySelf,
  projectId,
  commentId,
  commenterName,
  replyId,
  payload,
}: UseReplyMenuProps) => {
  const { openModal, closeModal } = useModalState();

  const replyUpdateMutation = useUpdateReplyMutation();
  const replyDeleteMutation = useDeleteReplyMutation();

  // REPLY UPDATE
  const handleUpdateReply = async (content: string) => {
    if (!projectId || !commentId || !replyId) return;

    try {
      await replyUpdateMutation.mutateAsync({
        projectId,
        commentId,
        replyId,
        content,
      });

      ToastSuccess(COMMENT_MESSAGE.REPLY_EDITED);

      closeModal();
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    }
  };

  const handleClickEditReply = () => {
    openModal({
      CustomComponent: (
        <CommentForm
          headerTitle={`Edit replying to ${commenterName}`}
          payload={payload}
          onSubmitComment={(text) => handleUpdateReply(text)}
        />
      ),
    });
  };

  // REPLY DELETE
  const handleDeleteReply = async () => {
    if (!projectId || !commentId || !replyId) return;

    try {
      await replyDeleteMutation.mutateAsync({
        projectId,
        commentId,
        replyId,
      });

      ToastSuccess(COMMENT_MESSAGE.REPLY_DELETED);

      closeModal();
    } catch (error) {
      ToastError(ERROR_MESSAGE.SERVER_ERROR);
    }
  };

  const handleClickDeleteReply = () => {
    openModal({
      CustomComponent: (
        <ConfirmModal
          modalTitle="Confirm Reply Deletion"
          modalDescription="Are you sure you want to delete your reply?"
          cancelButton={{
            onClickCancel: closeModal,
          }}
          confirmButton={{
            buttonText: "Delete",
            onClickConfirm: handleDeleteReply,
          }}
        />
      ),
    });
  };

  // NOTE: 내가 쓴 REPLY에 대한 MENU 오픈
  const handleClickCommenterMenu = () => {
    openModal({
      CustomComponent: (
        <CommentMenuBottomSheet
          commentType="REPLY"
          onClickEdit={handleClickEditReply}
          onClickDelete={handleClickDeleteReply}
        />
      ),
    });
  };

  // NOTE: 다른 유저가 쓴 REPLY에 대한 MENU 오픈
  const handleClickOtherMenu = () => {
    if (!replyId) return;

    openModal({
      CustomComponent: (
        <CommentOtherMenuBottomSheet commentType="REPLY" targetId={replyId} />
      ),
    });
  };

  return {
    handleClickMenu: isMySelf ? handleClickCommenterMenu : handleClickOtherMenu,
  };
};
